import {
  CheckboxInput,
  Collapsible,
  Flex,
  FlexColumn,
  FlexGrow,
  MultiSelectInput,
  Tooltip,
  useDownloadFile,
  formatNumber,
  ListContainer,
  ListTitleBar,
  ListInfoContainer,
  CloseInfo,
  useStyledTheme,
  WarningIcon,
  FormConfigurationProvider,
  useAsyncFunctionProgress,
} from '@equifinance/shared';
import {
  useEffect, useState, ReactNode,
} from 'react';
import styled, { css } from 'styled-components';
import { DocumentData } from '../../api/useDocumentsApi';
import useProductsApi, {
  MinimumValuationTypeRequired, ProductType,
} from '../../api/useProductsApi';
import { DownloadIcon, DownloadLink, HiddedA } from './ProductComponents';
import { useAccountContext, useProductsContext } from '../../contexts';
import ProductsTooltipInfoIcon from '../Icons/ProductsTooltipInfoIcon';
import Section from '../Account/components/Section';
import useDownloadEsisLink from './hooks/useDownloadEsisLink';
import { ToggleButtons } from '../Buttons/ToggleButton';
import { CrossCircleIcon, TickCircleIconInverted } from '../Account/components/Icons';

const ProductsInfoContainer = styled(Flex)`
  justify-content:space-between;
  width:100%;
  flex-wrap: wrap;
`;

const ProductSelectContainer = styled(FlexColumn)`${({ theme }) => `
  justify-content:center;
  gap:${theme.padding[1]};
  padding-right:${theme.padding[2]};
`}`;

const ProductTitleContainer = styled(Flex)`
  width: 100%;
`;

const InfoColumn = styled(FlexColumn)`
  width:20%;

  &:last-child {
    justify-content: space-evenly;
  }
`;

const CollapsibleInner = styled(FlexColumn)`${({ theme }) => `
  gap:${theme.padding[4]};
`}`;

const ProductSettingsContainer = styled(Flex)`
  align-items:center;
`;

const ProductFilterContainter = styled(FlexGrow)`${({ theme }) => `
  display: flex;
  gap: ${theme.padding[4]};
  align-items: center;
`}`;

const ProductsCollapsibleContainer = styled(Flex)`
  ${InfoColumn} {
    width: 25%;
  }
`;

const PaddedColumn = styled.i<{ isRight?: boolean }>`${({ theme, isRight = false }) => `
  ${isRight ? `margin-inline: ${theme.padding[6]}` : `margin-left: ${theme.padding[9]}`};
`}`;

const selectedHeaderCss = (hasErrors?: boolean) => css`${({ theme }) => `
  background: ${hasErrors ? theme.colours.danger[2] : theme.colours.primary[11]};
  padding-block: 0;
  .mi-chevron-down, .mi-chevron-up {
    color: ${theme.colours.primary[1]};
  };
`}`;

const listHeaderCss = css`${({ theme }) => `
  background: ${theme.colours.neutral[9]};
  padding-block: 0;
`}`;

const multiSelectInputCss = css`
  font-size: 12px;
  font-weight: 600;
  width: max-content;
`.toString();

const listBodyCss = (isSelected?: boolean) => css`${({ theme }) => `
  background: ${isSelected ? theme.colours.primary[11] : theme.colours.neutral[8]};
  box-shadow:inset 0px 10px 10px -10px rgba(1, 50, 123, 0.15);
  padding: ${theme.padding[2]} ${theme.padding[6]};
`}`;

const collapsibleBodyCss = css`
  padding: 0;
`;

const collapsibleBodyContentCss = css`${({ theme }) => `
  padding: ${theme.padding[5]};
  width: auto;
  gap:${theme.padding[4]};
`}`;

const InfoText = styled.p`${({ theme }) => `
  font-family: 'Open Sans';
  font-size: ${theme.fontSize[1]};
  line-height: ${theme.fontSize[3]};
`}`;

const InfoLabel = styled(InfoText) <{ isHeaderStyles?: boolean }>`${({ isHeaderStyles, theme }) => `
  display: flex;
  align-items: center;
  gap: ${theme.padding[1]};
  font-weight: ${theme.fontWeight.bold};
  color: ${isHeaderStyles ? theme.colours.primary[1] : theme.colours.neutral[2]};
`}`;

const InfoValue = styled(InfoText)`${({ theme }) => `
  font-weight: ${theme.fontWeight.normal};
  color: ${theme.colours.neutral[2]};
`}`;

const IconContainer = styled.div`${({ theme }) => `
  width: ${theme.fontSize[3]};
  height: ${theme.fontSize[3]};
`}`;

const FilterCheckboxContainer = styled(Flex)`${({ theme }) => `
  gap ${theme.padding[4]};
`}`;

const checkboxContainerStyles = css`
  height: auto;
`.toString();

export function Info({
  label,
  value,
  id = '',
  prefix,
  suffix,
  labelIcon,
  isNA,
  isHeading,
  isSelected,
  isFirstChild,
  isLastChild,
  hasErrors,
}: Readonly<{
  label: string,
  value: string | number,
  id?: string,
  prefix?: string,
  suffix?: string,
  labelIcon?: ReactNode,
  isNA?: boolean,
  isHeading?: boolean,
  isSelected?: boolean,
  isFirstChild?: boolean,
  isLastChild?: boolean,
  hasErrors?: boolean,
}>) {
  if (value && typeof value === 'number') {
    value = formatNumber(value);
  }

  return (
    <ListInfoContainer id={id} isHeading={isHeading} isFirstChild={isFirstChild} isSelected={isSelected} isLastChild={isLastChild} hasErrors={hasErrors}>
      <InfoLabel isHeaderStyles={isHeading}>
        <span>{`${label}:`}</span>
        {labelIcon}
      </InfoLabel>
      <InfoValue>
        {value ? (
          <>
            {prefix}
            {value}
            {suffix}
          </>
        ) : <>{isNA ? 'N/A' : '-'}</>}
      </InfoValue>
    </ListInfoContainer>
  );
}

export enum ProductTypes {
  Fixed = 'Fixed',
  Variable = 'Variable',
}

function ProductItem({
  product,
  productIndex,
  selectedProductId,
  onSelectProduct = () => { },
  hasProductSelectionOverriden = false,
  downloadESIS = async () => { },
  collapsed = true,
}: {
  product: ProductType,
  productIndex: number,
  selectedProductId: string,
  hasProductSelectionOverriden: boolean,
  onSelectProduct: (id: string, index: number) => void,
  downloadESIS: (product: ProductType) => Promise<void>,
  collapsed: boolean,
}) {
  const theme = useStyledTheme();
  const productErrors = product.errors;
  const productWarnings = product.warnings;

  const hasProductErrors = (productErrors?.length ?? 0) > 0;
  const hasProductWarnings = (productWarnings?.length ?? 0) > 0;
  const { isDisabled } = useDownloadEsisLink();

  const { inProgress, execute: executeDownloadESIS } = useAsyncFunctionProgress(downloadESIS);

  return (
    <Collapsible
      key={product.id}
      id={`${product.id}-product-details`}
      level={2}
      collapsed={collapsed}
      headerCss={product.id === selectedProductId ? selectedHeaderCss(hasProductErrors) : listHeaderCss}
      bodyCss={listBodyCss(product.id === selectedProductId)}
      title={(
        <ProductTitleContainer>
          <ProductSelectContainer>
            {(hasProductErrors || hasProductWarnings) && (
              <>
                <Tooltip
                  id={`errors-warnings-${product.id}`}
                >
                  <p>
                    {`Product ${hasProductErrors
                      ? 'is not eligible'
                      : 'requires referral'} because of the following reason(s):`}
                  </p>
                  <ul>
                    {hasProductErrors
                      ? product.errors.map((error) => (
                        <li key={`${error.errorMessage}-${product.id}`}>
                          {error.errorMessage}
                        </li>
                      ))
                      : product.warnings.map((error) => (
                        <li key={`${error.errorMessage}-${product.id}`}>
                          {error.errorMessage}
                        </li>
                      ))}
                  </ul>
                </Tooltip>
                <IconContainer id={`errors-warnings-${product.id}`}>
                  <WarningIcon
                    stroke={hasProductErrors
                      ? theme.colours.danger[1]
                      : theme.colours.secondary[1]}
                    fill={theme.colours.neutral[10]}
                  />
                </IconContainer>
              </>
            )}
            <CheckboxInput
              id={`${product.id}-select-product`}
              value={product.id === selectedProductId}
              disabled={hasProductSelectionOverriden || hasProductErrors}
              onChange={() => onSelectProduct(product.id, productIndex)}
              styles={product.id === selectedProductId && hasProductSelectionOverriden ? `background: ${theme.colours.primary[1]}; border-color: ${theme.colours.primary[1]};` : ''}
              containerStyles={checkboxContainerStyles}
            />
          </ProductSelectContainer>
          <ListTitleBar>
            <InfoColumn>
              <Tooltip
                id={`initial-pmt-${product.id}`}
                html="Initial monthly payment"
              />
              <Info
                id={`initial-pmt-${product.id}`}
                prefix="£"
                label="Initial pmt"
                value={product.initialMonthlyPayment}
                isHeading
                isSelected={product.id === selectedProductId}
                isFirstChild
                hasErrors={hasProductErrors}
              />
            </InfoColumn>
            <InfoColumn><Info label="Product type" value={product.productType} isHeading /></InfoColumn>
            <InfoColumn><Info label="Initial rate" value={product.initialRate} isHeading /></InfoColumn>
            <InfoColumn><Info label="Consent" value={product.consentRequired ? 'Yes' : 'No'} isHeading /></InfoColumn>
            <InfoColumn><Info label="ERCs" value={product.ercProfile !== 'None' ? 'Yes' : 'No'} isHeading /></InfoColumn>
            <InfoColumn>
              <ListInfoContainer>
                {product.canDownloadESIS && (
                  <Flex>
                    <DownloadIcon className="mi-download" disabled={isDisabled || inProgress} />
                    <DownloadLink
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isDisabled || inProgress) return;
                        executeDownloadESIS(product);
                      }}
                      aria-hidden="true"
                      disabled={isDisabled || inProgress}
                    >
                      Download ESIS
                    </DownloadLink>
                  </Flex>
                )}
              </ListInfoContainer>
            </InfoColumn>
          </ListTitleBar>
        </ProductTitleContainer>
      )}
    >
      <ProductsCollapsibleContainer>
        <PaddedColumn />
        <ProductsInfoContainer>
          <InfoColumn>
            <Info label="APRC rate" suffix="%" value={product.aprc} />
            <Info label="Lender fee" prefix="£" value={product.lenderFee} />
            <Info label="Total repayable over term" prefix="£" value={product.totalAmountRepayable} />
          </InfoColumn>
          <InfoColumn>
            <Info label="Reversionary Pmt" prefix="£" value={product?.reversionaryMonthlyPayment} />
            <Info label="Reversionary rate" value={product?.reversionaryRate} isNA={product?.productType === ProductTypes.Variable} />
            <Info
              id={`1st-charge-consent-required-${product.id}`}
              label="1st Charge Consent required"
              value={product.consentRequired ? 'Yes - where restriction applies' : 'No'}
              labelIcon={(
                <>
                  <ProductsTooltipInfoIcon id={`1st-charge-consent-required-${product.id}`} />
                  <Tooltip
                    id={`1st-charge-consent-required-${product.id}`}
                    html={product.consentRequiredTooltip}
                  />
                </>
              )}
            />
          </InfoColumn>
          <InfoColumn>
            <Info label="ERC" value={product.ercProfile} />
            <Info
              id={`overpayment-limit-${product.id}`}
              label="Overpayment limit"
              value={product.overpaymentDetails}
              labelIcon={(
                <>
                  <ProductsTooltipInfoIcon id={`overpayment-limit-${product.id}`} />
                  <Tooltip
                    id={`overpayment-limit-${product.id}`}
                    html={product.overpaymentTooltip}
                  />
                </>
              )}
            />
            <Info
              label="Valuation type"
              value={MinimumValuationTypeRequired[product.minimumValuationTypeRequired]}
            />
          </InfoColumn>
          <InfoColumn>
            <Info label="Broker fee" prefix="£" value={product.brokerFee} isLastChild />
            <Info label="Broker commission" prefix="£" value={product.brokerCommission} isLastChild />
          </InfoColumn>
        </ProductsInfoContainer>
        <PaddedColumn isRight />
      </ProductsCollapsibleContainer>
    </Collapsible>
  );
}

export default function Products({
  onProductSelected = () => { },
  onDownloadEsis,
}: Readonly<{
  onProductSelected?: (productId: string) => void,
  onDownloadEsis?: (applicationId: string, productId: string) => Promise<DocumentData>
}>) {
  const {
    account, id: accountId, fetchAccount, fetchProductsSummary, fetchTransitionStatus,
  } = useAccountContext();
  const { productsObject, fetchProducts, fetchProductLoanDetails } = useProductsContext();
  const { setSelectedProduct } = useProductsApi();

  const overridenProduct = productsObject?.products?.find(({ isSelected }) => (productsObject.isProductSelectionOverriden && isSelected));
  const hasProductSelectionOverriden = !!overridenProduct;
  const eligibleProducts = productsObject?.products?.filter(({ productId }) => (productId !== overridenProduct?.productId)) ?? [];

  const [selectedProductId, setSelectedProductId] = useState<string>('');

  const [expandedProductId, setExpandedProductId] = useState<string | null>(null);
  const [allExpanded, setAllExpanded] = useState<boolean[]>([]);

  const onSelectProduct = async (productId: string) => {
    onProductSelected(productId);
    setSelectedProductId(productId);
    await setSelectedProduct(accountId, productId);
    fetchAccount();
    fetchProductLoanDetails();
    await fetchTransitionStatus();
    setAllExpanded(Array(eligibleProducts.length ?? 0).fill(false));
    setExpandedProductId(expandedProductId === productId ? null : productId);
    if (selectedProductId !== productId) {
      fetchProductsSummary(accountId);
      fetchProducts();
    }
  };

  const expandAll = () => {
    setAllExpanded(Array(eligibleProducts.length ?? 0).fill(true));
    setExpandedProductId(null);
  };

  const collapseAll = () => {
    setAllExpanded(Array(eligibleProducts.length ?? 0).fill(false));
    setExpandedProductId(null);
  };

  const defaultProductFilter = Object.keys(ProductTypes);
  const [productFilter, setProductFilter] = useState({
    productTypes: defaultProductFilter,
    isNoConsent: false,
    isNoERC: false,
  });

  const onChangeProductTypes = (values: string[]) => {
    setProductFilter((prev) => ({
      ...prev,
      productTypes: values,
    }));
  };

  const onChangeConsentFilter = (value: boolean) => {
    setProductFilter((prev) => ({
      ...prev,
      isNoConsent: value,
    }));
  };

  const onChangeERCFilter = (value: boolean) => {
    setProductFilter((prev) => ({
      ...prev,
      isNoERC: value,
    }));
  };

  const onDeselectOption = (value: string) => {
    setProductFilter((prev) => ({
      ...prev,
      productTypes: prev.productTypes.filter((v) => v !== value),
    }));
  };

  const filteredProducts = eligibleProducts.filter((product) => {
    const { productType, consentRequired, ercProfile } = product;

    const matchesProductType = productFilter.productTypes.some(
      (filterValue) => productType.includes(filterValue),
    );

    const matchesConsentFilter = productFilter.isNoConsent
      ? !consentRequired
      : true;

    const matchesERCFilter = productFilter.isNoERC
      ? ercProfile === 'None'
      : true;

    return matchesProductType && matchesConsentFilter && matchesERCFilter;
  }) || [];

  const {
    downloadFile,
    downloadFileRef,
  } = useDownloadFile();

  const downloadESIS = async (product: ProductType) => {
    const document = await onDownloadEsis?.(accountId, product.id);

    if (document) {
      downloadFile(document.fileName, document.documentData);
    }
  };

  const productsInfoMessage = `Products available based on ${account.property.propertyValueType === 'BasedOnAvm' ? 'AVM' : "Applicant's estimate"}`;

  const autoRefreshProducts = async () => {
    const selectedProductId = productsObject?.products?.find((product) => product.isSelected)?.id ?? '';
    setSelectedProductId(selectedProductId);
    onProductSelected(selectedProductId);
    setAllExpanded(Array(productsObject?.products?.length ?? 0).fill(false));
  };

  useEffect(() => {
    autoRefreshProducts();
  }, [productsObject]);

  useEffect(() => {
    if (!productFilter.productTypes.length) {
      setProductFilter((prev) => ({
        ...prev,
        productTypes: defaultProductFilter,
      }));
    }
  }, [productFilter.productTypes.length]);

  return (
    <>
      <Section
        id={productsObject?.id}
        refName={`${accountId}-products`}
        title="Products"
        titleIcon={(!!selectedProductId && !Object.entries(productsObject?.errors ?? {})?.length) ? <TickCircleIconInverted /> : <CrossCircleIcon />}
        bodyCss={collapsibleBodyCss}
        bodyContentCss={collapsibleBodyContentCss}
        errors={productsObject?.errors ?? {}}
        warnings={productsObject?.warnings ?? {}}
      >
        <CollapsibleInner>
          {overridenProduct && (
            <ProductItem
              product={overridenProduct}
              productIndex={-1}
              selectedProductId={selectedProductId}
              hasProductSelectionOverriden={hasProductSelectionOverriden}
              onSelectProduct={onSelectProduct}
              downloadESIS={downloadESIS}
              collapsed
            />
          )}

          {eligibleProducts?.length > 0 && (
            <>
              <CloseInfo>{productsInfoMessage}</CloseInfo>
              <ProductSettingsContainer>
                <ProductFilterContainter>
                  <MultiSelectInput
                    id="product-filter"
                    value={productFilter.productTypes}
                    onChange={onChangeProductTypes}
                    options={ProductTypes}
                    hasSelectAll={false}
                    disableSearch
                    selectedLabel="Product type:"
                    onDeselectOption={onDeselectOption}
                    containerStyles={multiSelectInputCss}
                  />
                  <FilterCheckboxContainer>
                    <div>
                      <CheckboxInput
                        id="consent-product-filter"
                        label="No consent products only"
                        value={productFilter.isNoConsent}
                        onChange={onChangeConsentFilter}
                      />
                    </div>
                    <div>
                      <CheckboxInput
                        id="erc-product-filter"
                        label="No ERC products only"
                        value={productFilter.isNoERC}
                        onChange={onChangeERCFilter}
                      />
                    </div>
                  </FilterCheckboxContainer>
                </ProductFilterContainter>
                <FormConfigurationProvider isReadOnlyInput={false}>
                  <ToggleButtons onExpandAll={expandAll} onCollapseAll={collapseAll} />
                </FormConfigurationProvider>
              </ProductSettingsContainer>
              <ListContainer>
                {filteredProducts.map((product, productIndex) => (
                  <ProductItem
                    key={product.id}
                    product={product}
                    selectedProductId={selectedProductId}
                    productIndex={productIndex}
                    hasProductSelectionOverriden={hasProductSelectionOverriden}
                    onSelectProduct={onSelectProduct}
                    downloadESIS={downloadESIS}
                    collapsed={!(allExpanded.every((expandItem) => expandItem) || expandedProductId === product.id)}
                  />
                ))}
              </ListContainer>
            </>
          )}
        </CollapsibleInner>
      </Section>
      <HiddedA ref={downloadFileRef} />
    </>
  );
}
